import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { buildBookmarkletHref, buildInspectorBookmarkletHref, API_ORIGIN, CDN_ORIGIN, MATCH_API_URL, WIDGET_CDN_URL } from '../utils/bookmarklets';

interface WidgetConfig {
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  displayMode: 'thumbnails' | 'breadcrumb';
  displayTrigger: 'auto' | 'hover' | 'tap' | 'always';
  maxProducts: number;
  highlightColor: string;
  minImageSize: number;
}

const DEFAULTS: WidgetConfig = {
  position: 'bottom-right',
  displayMode: 'thumbnails',
  displayTrigger: 'auto',
  maxProducts: 3,
  highlightColor: '#007bff',
  minImageSize: 100,
};

// What the publisher actually changed, as the widget expects it. ONE function,
// because the pasted snippet and the preview bookmarklet must not disagree about
// which settings are in force — they did until 2026-09-06, when the preview took
// the API key alone and silently ran defaults.
function nonDefaultOverrides(config: WidgetConfig): Record<string, unknown> {
  const overrides: Record<string, unknown> = {};

  const overlay: Record<string, unknown> = {};
  if (config.position !== DEFAULTS.position) overlay.position = config.position;
  if (config.displayMode !== DEFAULTS.displayMode) overlay.displayMode = config.displayMode;
  if (config.displayTrigger !== DEFAULTS.displayTrigger) overlay.displayTrigger = config.displayTrigger;
  if (config.maxProducts !== DEFAULTS.maxProducts) overlay.maxProducts = config.maxProducts;
  if (config.highlightColor !== DEFAULTS.highlightColor) overlay.highlightColor = config.highlightColor;
  if (Object.keys(overlay).length > 0) overrides.overlay = overlay;

  if (config.minImageSize !== DEFAULTS.minImageSize) {
    overrides.targeting = { minImageSize: config.minImageSize };
  }
  return overrides;
}

function buildSnippet(apiKey: string, config: WidgetConfig): string {
  // Always pin apiUrl so pasted snippets work regardless of widget-build defaults
  const configObj: Record<string, unknown> = { apiKey, apiUrl: MATCH_API_URL, ...nonDefaultOverrides(config) };

  const configJson = JSON.stringify(configObj, null, 2);

  // Config first, loader `async` second. The old form was two SYNCHRONOUS
  // tags — the loader, then `new ImgAff(...)` depending on its global — so the
  // browser had to stop parsing the page and fetch the widget before rendering
  // anything below the snippet. Measured on a live article the tag sits ~28%
  // of the way down the document, so that stall delayed roughly 72% of the
  // page, right before Largest Contentful Paint.
  //
  // Declaring the config first lets the widget self-initialise whenever it
  // arrives, so `async` is safe and the page never waits for us.
  // The two preconnect hints are deliberately DIFFERENT shapes and swapping them makes
  // both useless. api.imgaff.com is reached by a credential-less fetch(), which lands in
  // the ANONYMOUS connection pool -> needs `crossorigin`. cdn.imgaff.com is reached by a
  // classic <script> with no crossorigin attribute, which uses the PLAIN pool -> must not
  // carry it. Measured: the first match request of a pageview cost 248ms against 126ms
  // for the second — handshake AND CORS preflight in unknown proportion, since the second
  // fell inside the ~5s preflight cache. This hint addresses the handshake; the API's
  // Access-Control-Max-Age addresses the preflight. The widget's impression beacon does
  // not cover either: it is still in flight when the match request leaves.
  return `<link rel="preconnect" href="${API_ORIGIN}" crossorigin>
<link rel="preconnect" href="${CDN_ORIGIN}">
<script>
  window.imgaffConfig = ${configJson};
</script>
<script async src="${WIDGET_CDN_URL}"></script>`;
}

const EmbedCodeGenerator: React.FC = () => {
  const { verificationStatus } = useAuth();
  // Identical rule to ActivationChecklist, deliberately: null means UNKNOWN
  // (profile not loaded yet, or its fetch failed) and must be non-blocking, or
  // an already-verified account gets warned forever.
  const verifyBlocks = verificationStatus != null && verificationStatus !== 'verified';
  const [apiKey, setApiKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<WidgetConfig>({ ...DEFAULTS });
  // Snippet-first (portal-ux-clarity): appearance/behavior options live
  // behind this collapsed disclosure so the default path leads.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<'key' | 'snippet' | 'bookmarklet' | 'inspector' | null>(null);

  useEffect(() => {
    apiService.getApiKey()
      .then(setApiKey)
      // Never "contact support": on the commonest path to this failure there
      // is nothing wrong and nobody to contact. See the render guard below.
      .catch(() => setError('We could not load your API key just now. Reload the page to try again.'))
      .finally(() => setLoading(false));
  }, []);

  // The preview runs what the controls above it say, not the defaults.
  const bookmarkletHref = apiKey ? buildBookmarkletHref(apiKey, nonDefaultOverrides(config)) : '';
  // No API key needed — it reads the widget instance already running on the
  // page (via the window.__imgaffDiagnostics facade), never constructs a new one.
  const inspectorBookmarkletHref = buildInspectorBookmarkletHref();

  // React blocks javascript: URLs in JSX hrefs; set it imperatively so the
  // anchor stays draggable to the bookmarks bar.
  //
  // THE JSX HREF MUST THEREFORE BE A PLACEHOLDER, and until 2026-09-06 it was
  // not: both anchors ALSO passed the javascript: URL through JSX, so React
  // logged "A future version of React will block javascript: URLs" on every
  // visit to this page.
  //
  // CALLBACK REFS, NOT EFFECTS, and that is load-bearing. These used to be two
  // useEffects keyed on the href value, which worked for the PREVIEW anchor by
  // accident — its href changes from '' to the real URL when the key arrives,
  // so the effect re-ran after the anchor existed. The INSPECTOR's href is a
  // constant, so its effect ran exactly once, on the render where `loading` was
  // still true and the anchor was not mounted: the ref was null, the setAttribute
  // never happened, and the effect had been dead code for its whole life. It was
  // invisible because the JSX href was silently doing the work. Removing that
  // exposed it — an empty inspector bookmarklet — and only driving the page
  // showed it, since the suite covered the preview anchor and not this one.
  //
  // A callback ref fires when the node mounts AND whenever the href changes, so
  // neither anchor can depend on render order.
  const setBookmarkletHref = useCallback((node: HTMLAnchorElement | null) => {
    if (node && bookmarkletHref) node.setAttribute('href', bookmarkletHref);
  }, [bookmarkletHref]);

  const setInspectorHref = useCallback((node: HTMLAnchorElement | null) => {
    if (node) node.setAttribute('href', inspectorBookmarkletHref);
  }, [inspectorBookmarkletHref]);

  const copyToClipboard = useCallback(async (text: string, field: 'key' | 'snippet' | 'bookmarklet' | 'inspector') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  }, []);

  const snippet = apiKey ? buildSnippet(apiKey, config) : '';

  if (loading) {
    return <div style={{ padding: '2rem' }}>Loading...</div>;
  }

  return (
    <div className="embed-generator" style={{ maxWidth: '800px' }}>
      <h2>Install ImgAff</h2>
      <p style={{ color: 'var(--muted)', marginBottom: '1.5rem' }}>
        Add ImgAff to your verified site after connecting a product feed. WordPress publishers can install{' '}
        <a href="https://wordpress.org/plugins/imgaff/" target="_blank" rel="noopener noreferrer">the ImgAff plugin</a>{' '}
        instead of pasting code; this page provides the manual installation and preview tools.
      </p>

      {/* Suppressed while the site is unverified, because THERE IS NO KEY YET
          and that is the designed state: ensureApiKeyForUser mints one at
          verification, so a keyless account before that point is normal, not
          broken. Showing the red banner there stacked a "something failed,
          get help" on top of the amber panel below that correctly explains
          what to do next — with the wrong diagnosis on top, and no support
          desk to contact. Same shape as the 404 "contact your administrator"
          this page already fixed once. */}
      {error && !verifyBlocks && (
        <div style={{ padding: '1rem', backgroundColor: 'var(--danger-bg)', borderRadius: 'var(--radius)', color: 'var(--danger)', marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {/* The trap this closes, found by walking signup end-to-end on production:
          a new publisher lands here, copies a snippet that looks complete,
          pastes it on their site — and every match request comes back
          403 "Account verification required". On their page: nothing. No
          overlay, no error, no explanation. The likeliest first support ticket
          is "I installed it and nothing happens", from someone who did exactly
          what this page told them to.

          The Feeds page already gets this right (its Upload button is visibly
          disabled until verification); Install had no equivalent signal — only
          a nav link and one clause in a sentence.

          The copy buttons are deliberately NOT disabled. Verification is a file
          upload to the site root, so "paste the snippet, then verify" is a
          legitimate order, and blocking the copy would break it. What was
          missing is not a gate, it is an explanation: the failure mode here is
          SILENCE, and silence is what this banner removes.

          verifyBlocks mirrors ActivationChecklist exactly, including the reason
          null is non-blocking: an unknown status (profile not loaded, or its
          fetch failed) must not nag an account that is already verified. */}
      {verifyBlocks && (
        <div
          data-testid="embed-verify-warning"
          style={{
            padding: '0.9rem 1rem',
            marginBottom: '1.5rem',
            border: '1px solid var(--warning, #B45309)',
            borderLeft: '4px solid var(--warning, #B45309)',
            borderRadius: 'var(--radius)',
            background: 'var(--warning-bg, #FFFBEB)'
          }}
        >
          <strong>This key will not serve matches yet.</strong>
          <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
            Your site is not verified, so match requests are refused and no overlays
            appear — even when the code is installed correctly. You can install now,
            but nothing will show until verification completes.{' '}
            {/* PLAIN ANCHOR ON PURPOSE, and not an oversight this pass missed:
                App.tsx records the same decision for the header badge — the
                browser scrolls to #website-verification natively on load, and a
                client-side navigation to a hash does not, since nothing here
                configures scroll restoration. A `Link` would land the publisher
                at the top of the dashboard with the verification block offscreen.
                The /debug link above has no hash and is a Link. */}
            <a href="/#website-verification">Verify your site</a>.
          </div>
        </div>
      )}

      {/* API Key Section */}
      <section style={{ marginBottom: '2rem' }}>
        <h3>Your API Key</h3>
        <div className="compact-action-row" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* An empty readonly box reads as a rendering failure. Say what the
              blank means instead — the key does not exist yet, and the reason
              is one line further down the page. */}
          <input
            data-testid="api-key-display"
            type="text"
            readOnly
            value={apiKey}
            placeholder={verifyBlocks ? 'Issued once your site is verified' : ''}
            style={{
              flex: 1,
              padding: '0.5rem',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius)',
              backgroundColor: 'var(--surface-alt)',
            }}
          />
          <button
            data-testid="copy-api-key"
            onClick={() => copyToClipboard(apiKey, 'key')}
            disabled={!apiKey}
            className={`copy-btn${copiedField === 'key' ? ' is-copied' : ''}`}
            style={{ whiteSpace: 'nowrap' }}
          >
            {copiedField === 'key' ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </section>

      {/* Generated Snippet Section — the default path, first */}
      <section style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Manual embed code</h3>
          <button
            data-testid="copy-snippet"
            onClick={() => copyToClipboard(snippet, 'snippet')}
            disabled={!snippet}
            className={`copy-btn${copiedField === 'snippet' ? ' is-copied' : ''}`}
          >
            {copiedField === 'snippet' ? 'Copied!' : 'Copy Snippet'}
          </button>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          Paste this code before the closing <code>&lt;/body&gt;</code> tag on your website.
        </p>
        <pre
          data-testid="snippet-output"
          style={{
            padding: '1rem',
            backgroundColor: 'var(--ink-soft)',
            color: 'var(--line)',
            borderRadius: 'var(--radius)',
            overflow: 'auto',
            fontSize: '0.85rem',
            lineHeight: 1.5,
          }}
        >
          {/* Without this the block renders as an empty dark bar, which reads
              as a broken component rather than as "there is nothing to show
              yet". The snippet cannot exist before the key does. */}
          {snippet || 'Your embed code appears here once your site is verified and a key is issued.'}
        </pre>
      </section>

      {/* Advanced options — collapsed by default; the snippet above works
          as-is with widget defaults */}
      <section style={{ marginBottom: '2rem' }}>
        <button
          type="button"
          data-testid="advanced-options-toggle"
          aria-expanded={advancedOpen}
          aria-controls="advanced-options-content"
          onClick={() => setAdvancedOpen(o => !o)}
          className="btn btn--secondary"
        >
          Advanced options (position, display, colors) {advancedOpen ? '▲' : '▼'}
        </button>
        {advancedOpen && (
        <div id="advanced-options-content" data-testid="advanced-options-content" style={{ marginTop: '1rem' }}>
        <div className="settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label htmlFor="overlay-position" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Overlay Position
            </label>
            <select
              id="overlay-position"
              data-testid="config-position"
              value={config.position}
              onChange={(e) => setConfig({ ...config, position: e.target.value as WidgetConfig['position'] })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--line)' }}
            >
              <option value="top-left">Top Left</option>
              <option value="top-right">Top Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-right">Bottom Right</option>
            </select>
          </div>

          <div>
            <label htmlFor="display-mode" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Display Mode
            </label>
            <select
              id="display-mode"
              data-testid="config-display-mode"
              value={config.displayMode}
              onChange={(e) => setConfig({ ...config, displayMode: e.target.value as WidgetConfig['displayMode'] })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--line)' }}
            >
              <option value="thumbnails">Thumbnails</option>
              <option value="breadcrumb">Breadcrumb</option>
            </select>
          </div>

          <div>
            <label htmlFor="display-trigger" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Display Trigger
            </label>
            <select
              id="display-trigger"
              data-testid="config-display-trigger"
              value={config.displayTrigger}
              onChange={(e) => setConfig({ ...config, displayTrigger: e.target.value as WidgetConfig['displayTrigger'] })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--line)' }}
            >
              <option value="auto">Automatic (overlays appear as images scroll into view)</option>
              <option value="hover">Hover only</option>
              <option value="tap">Tap to reveal</option>
              <option value="always">Always visible (same as automatic)</option>
            </select>
          </div>

          <div>
            <label htmlFor="max-products" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Max Products ({config.maxProducts})
            </label>
            <input
              id="max-products"
              data-testid="config-max-products"
              type="range"
              min={1}
              max={5}
              value={config.maxProducts}
              onChange={(e) => setConfig({ ...config, maxProducts: Number(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label htmlFor="highlight-color" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Highlight Color
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                id="highlight-color"
                data-testid="config-highlight-color"
                type="color"
                value={config.highlightColor}
                onChange={(e) => setConfig({ ...config, highlightColor: e.target.value })}
              />
              <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{config.highlightColor}</span>
            </div>
          </div>

          <div>
            <label htmlFor="min-image-size" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Min Image Size ({config.minImageSize}px)
            </label>
            <input
              id="min-image-size"
              data-testid="config-min-image-size"
              type="range"
              min={50}
              max={500}
              step={10}
              value={config.minImageSize}
              onChange={(e) => setConfig({ ...config, minImageSize: Number(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        </div>
        )}
      </section>

      {/* NAME THE DEVICE. Both sections below work by dragging a button to a
          bookmarks bar, which a phone does not have — and at 390px they are
          739px, 37% of this page, of instructions that cannot be followed on
          the device holding them. Neither said so.

          The note is unconditional rather than shown only at narrow widths: it
          is a fact about the tools, not about the viewport, and a publisher
          reading this on a laptop to install on a phone-only CMS needs it too.

          It points at /debug because that IS the device-independent one — a
          single button that opens the publisher's own site with the debug
          sidebar via a short-lived token, so it needs no dragging and no
          desktop. It was mentioned once on this page, in passing, inside the
          inspector paragraph below.

          NAMED AS THE NAV NAMES IT — "Fix matches", not "Troubleshooting". A
          publisher told to use Troubleshooting scans the nav for a word that is
          not there. Settled portal-wide on 2026-09-06 (E5): the nav label and
          the document title name the PLACE, `/debug`'s own button names the
          ACTION ("Troubleshoot my live site"), and every SIGNPOST to that
          screen uses the place's name. */}
      <p
        data-testid="bookmarklet-device-note"
        style={{ marginTop: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}
      >
        The two tools below are dragged to your browser's bookmarks bar, so they need a
        computer. On a phone, <Link to="/debug">Fix matches</Link> does the same job from here —
        it opens your site with the debug sidebar, nothing to drag.
      </p>

      {/* Bookmarklet Preview Section */}
      <section style={{ marginTop: '2rem' }}>
        <h3>Preview on your verified site</h3>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
          Drag the button below to your bookmarks bar, then open your website and click it —
          the widget loads with your API key and overlays appear on matching images, without
          touching your template. The preview only works on your registered domain
          ({'the API rejects other sites'}); if your site blocks injected scripts (strict CSP),
          use the embed code above instead.
        </p>
        <div className="compact-action-row" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <a
            ref={setBookmarkletHref}
            href="#preview-unavailable"
            /* Placeholder only — the real javascript: URL is set imperatively
               above, so nothing unsafe passes through JSX. */
            data-testid="bookmarklet-link"
            onClick={(e) => {
              e.preventDefault();
              window.alert('Drag this button to your bookmarks bar, then click it while viewing your own website.');
            }}
            className="bookmarklet-btn"
          >
            Preview ImgAff
          </a>
          <button
            data-testid="copy-bookmarklet"
            onClick={() => copyToClipboard(bookmarkletHref, 'bookmarklet')}
            disabled={!apiKey}
            className={`copy-btn${copiedField === 'bookmarklet' ? ' is-copied' : ''}`}
          >
            {copiedField === 'bookmarklet' ? 'Copied!' : 'Copy bookmarklet code'}
          </button>
        </div>
      </section>

      {/* Inspector Bookmarklet Section — part of the troubleshooting concept,
          while the preview bookmarklet above stays under installation.
          A NAV LABEL NAMES A PLACE; A BUTTON NAMES AN ACTION. This link is a
          SIGNPOST, so it carries the place's name as the nav shows it — "Fix
          matches". `/debug`'s own button still reads "Troubleshoot my live
          site", which is correct: it says what pressing it does. What was wrong
          until 2026-09-06 was a signpost pointing at a word the nav does not
          contain. */}
      <section style={{ marginTop: '2rem' }}>
        <h3>Inspect which images ImgAff can use</h3>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
          Once the widget is running on your site (via the embed code or the preview bookmarklet
          above), drag this button to your bookmarks bar and click it — every image on the page
          gets outlined with a status badge: matched (green), eligible/sent (yellow), or skipped
          with the reason (red). Read-only — nothing is uploaded or stored, and your API key is
          never exposed by this bookmarklet. For a full diagnosis of your live site, use{' '}
          <Link to="/debug">Fix matches</Link>.
        </p>
        <div className="compact-action-row" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <a
            ref={setInspectorHref}
            href="#inspector-unavailable"
            /* Placeholder only — see the preview anchor above. */
            data-testid="inspector-bookmarklet-link"
            onClick={(e) => {
              e.preventDefault();
              window.alert('Drag this button to your bookmarks bar, then click it on a page where the ImgAff widget is running.');
            }}
            className="bookmarklet-btn"
          >
            Inspect ImgAff
          </a>
          <button
            data-testid="copy-inspector-bookmarklet"
            onClick={() => copyToClipboard(inspectorBookmarkletHref, 'inspector')}
            className={`copy-btn${copiedField === 'inspector' ? ' is-copied' : ''}`}
          >
            {copiedField === 'inspector' ? 'Copied!' : 'Copy inspector code'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default EmbedCodeGenerator;
